import React, { useCallback, useState } from 'react';
import { Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import { taskTemplateApi } from '@/lib/taskTemplateApi';
import { useModalStore, useTaskTemplatesStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import useAndroidDetailBack from '@/hooks/useAndroidDetailBack';

function normalizeTaskTemplateSlug(raw) {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw) && raw[0]) return String(raw[0]);
  return '';
}

const TaskTemplateDetailNavigationHeaderLeft = React.memo(() => {
  const router = useRouter();
  const { t } = useTranslation();
  useAndroidDetailBack(router, '/tasks');

  const onBack = useCallback(() => {
    // Drawer navigation gives native detail screens an unrelated back entry
    // (often Home). Keep browser history on web, but make the explicitly
    // labelled native back button return to the Tasks hub every time.
    if (Platform.OS === 'web' && router.canGoBack?.()) {
      router.back();
      return;
    }
    router.replace('/tasks');
  }, [router]);

  return (
    <NavigationBarButtonContainer leftPadding>
      <NavigationBarButton
        icon="chevron-left"
        tooltip={t('nav.routes.taskTemplate.backToTasksTitle')}
        accessibilityLabel={t('nav.routes.taskTemplate.backToTasks')}
        onPress={onBack}
      />
    </NavigationBarButtonContainer>
  );
});

TaskTemplateDetailNavigationHeaderLeft.displayName = 'TaskTemplateDetailNavigationHeaderLeft';

const TaskTemplateDetailNavigationHeaderRight = React.memo(({ slug }) => {
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const templates = useTaskTemplatesStore((s) => s.templates);
  const { openModal } = useModalStore((s) => s.actions);
  const requireAuth = useRequireAuth();
  const [busy, setBusy] = useState(false);

  const onSchedule = useCallback(async () => {
    if (!slug || busy) return;
    let template = templates.find((tpl) => tpl.slug === slug);
    const openSetup = (tpl) => {
      requireAuth(
        () => openModal('SetupTaskFromTemplateModal', { template: tpl }),
        {
          source: 'task_template_detail_header_setup',
          title: `Sign in to set up ${tpl?.name || 'this task'}`,
          subtitle: 'Scheduled tasks run on your account so we know when to start them and where to send the output.',
          icon: 'calendar-clock',
        },
      );
    };
    if (template) {
      openSetup(template);
      return;
    }
    setBusy(true);
    try {
      template = await taskTemplateApi.getTemplateBySlug(slug);
    } catch {
      template = null;
    } finally {
      setBusy(false);
    }
    if (template) {
      openSetup(template);
    }
  }, [slug, busy, templates, openModal, requireAuth]);

  const label = t('tasks.setUpThisTask');

  return (
    <NavigationBarButtonContainer rightPadding>
      <NavigationBarButton
        icon={busy ? 'spinner' : 'calendar-check'}
        solidIcon={busy}
        tooltip={t('tasks.setUpThisTask')}
        label={isXS ? undefined : label}
        onPress={onSchedule}
        disabled={!slug || busy}
      />
    </NavigationBarButtonContainer>
  );
});

TaskTemplateDetailNavigationHeaderRight.displayName = 'TaskTemplateDetailNavigationHeaderRight';

/**
 * /tasks/template/[slug] — SEO template detail + schedule CTA in header.
 * @param {string} title — header title (template name or fallback)
 */
export function getTaskTemplateDetailRouteNavigationOptions(title, routeSlug) {
  // Header components can inherit the layout's params rather than the detail
  // screen's params. Pass the selected route through explicitly.
  const slug = normalizeTaskTemplateSlug(routeSlug);
  return {
    title,
    headerTitle: title,
    headerBackVisible: false,
    headerShown: true,
    headerLeft: () => <TaskTemplateDetailNavigationHeaderLeft />,
    headerRight: () => <TaskTemplateDetailNavigationHeaderRight slug={slug} />,
  };
}
